import { useMemo, useState } from "react"
import { Box, Button, Flex, HStack, Input, Text, Badge, Checkbox, chakra } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import { Link as RouterLink } from "react-router-dom"
import SectionCard from "../../framework/SectionCard"
import DataTable from "../../framework/DataTable"
import { extractErrorInfo } from "../../lib/api/error-utils"
import { useResorts, type ResortRow } from "../../hooks/useResorts"
import ErrorAlert from "../../components/ErrorAlert"

const columnSortMap: Record<string, string> = {
  name: "name",
  liftCount: "lift_count",
  runCount: "run_count",
  totalRunLength: "total_run_length_m",
}

const ChakraRouterLink = chakra(RouterLink)

const columns: ColumnDef<ResortRow>[] = [
  {
    accessorKey: "name",
    header: "Resort",
    id: "name",
    enableSorting: true,
    cell: ({ row }) => {
      const { uid, name, isGroup } = row.original
      const label = name || uid
      const isOrphanedScraper = row.original.orphanedScraper

      return (
        <HStack gap="2">
          {isOrphanedScraper ? (
            <Text color="gray.100" fontWeight="semibold">
              {label}
            </Text>
          ) : (
            <ChakraRouterLink
              to={`/resorts/${uid}`}
              color="blue.200"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              {label}
            </ChakraRouterLink>
          )}
          {isOrphanedScraper && (
            <Badge colorPalette="orange" variant="solid" fontSize="0.65rem">
              ORPHANED SCRAPER
            </Badge>
          )}
          {isGroup && (
            <Badge colorPalette="purple" variant="solid" fontSize="0.65rem">
              GROUP
            </Badge>
          )}
        </HStack>
      )
    },
  },
  {
    accessorKey: "liftCount",
    header: "Lift Count",
    id: "liftCount",
    enableSorting: true,
  },
  {
    accessorKey: "runCount",
    header: "Run Count",
    id: "runCount",
    enableSorting: true,
  },
  {
    id: "totalRunLength",
    header: "Run length (km)",
    accessorFn: (row) => row.totalRunLengthMeters,
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>()
      return value ? `${Math.round(value / 1000)} km` : "—"
    },
    enableSorting: true,
  },
  {
    id: "views",
    header: "Views",
    cell: ({ row }) => (
      <HStack gap="2">
        <ChakraRouterLink 
          to={`/resorts/${row.original.uid}`} 
          color="blue.200" 
          fontSize="sm"
          _hover={{ textDecoration: "underline" }}
        >
          Info
        </ChakraRouterLink>
        <ChakraRouterLink 
          to={`/resorts/${row.original.uid}/map`} 
          color="blue.200" 
          fontSize="sm"
          _hover={{ textDecoration: "underline" }}
        >
          Map
        </ChakraRouterLink>
        <ChakraRouterLink 
          to={`/resorts/${row.original.uid}/features`} 
          color="blue.200" 
          fontSize="sm"
          _hover={{ textDecoration: "underline" }}
        >
          Features
        </ChakraRouterLink>
      </HStack>
    ),
  },
]

export default function ResortDashboard() {
  const [globalFilter, setGlobalFilter] = useState("")
  const [hasLiftOpeningIntegration, setHasLiftOpeningIntegration] = useState(false)
  const [hasSnowReportIntegration, setHasSnowReportIntegration] = useState(false)
  const [orphanedScrapers, setOrphanedScrapers] = useState(false)
  const [sorting, setSorting] = useState([{ id: "name", desc: false }])

  const currentSort = sorting[0] ?? { id: "name", desc: false }
  const sortField = columnSortMap[currentSort.id] ?? "name"
  const sortDir = currentSort.desc ? "desc" : "asc"

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useResorts({
    filter: globalFilter || undefined,
    sort: sortField,
    dir: sortDir,
    hasLiftOpeningIntegration,
    hasSnowReportIntegration,
    orphanedScrapers,
  })

  // Flatten all pages into a single array
  const resorts = useMemo(
    () => data?.pages.flatMap((page) => page.resorts) ?? [],
    [data]
  )

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <SectionCard>
      <Text fontSize="md" mb="3">
        Resorts
      </Text>
      <HStack gap="3" mb="4" flexWrap="wrap">
        <Input
          placeholder="Filter resorts"
          bg="gray.700"
          value={globalFilter}
          onChange={(event) => setGlobalFilter(event.target.value)}
        />
        <Checkbox.Root
          checked={hasLiftOpeningIntegration}
          onCheckedChange={(event) => setHasLiftOpeningIntegration(!!event.checked)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>Lift opening integrations only</Checkbox.Label>
        </Checkbox.Root>
        <Checkbox.Root
          checked={hasSnowReportIntegration}
          onCheckedChange={(event) => setHasSnowReportIntegration(!!event.checked)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>Snow scraper integrations only</Checkbox.Label>
        </Checkbox.Root>
        <Checkbox.Root
          checked={orphanedScrapers}
          onCheckedChange={(event) => setOrphanedScrapers(!!event.checked)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>Orphaned scrapers</Checkbox.Label>
        </Checkbox.Root>
      </HStack>

      {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

      <Box overflowX="auto">
        <DataTable
          columns={columns}
          data={resorts}
          isLoading={isLoading}
          emptyMessage="No resorts available."
          sorting={sorting}
          onSortingChange={setSorting}
          globalFilter={globalFilter}
          manualSorting={true}
        />
      </Box>

      {hasNextPage && (
        <Flex justify="flex-end" mt="4">
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        </Flex>
      )}
    </SectionCard>
  )
}
